/**
 * Cable profiles -- group 05.
 *
 * Vertere Acoustics, Kubala-Sosna, JPS Labs, Harmonic Technology, Acoustic Zen,
 * Morrow Audio, Signal Cable and Blue Jeans Cable.
 *
 * Seven of these eight makers publish no resistance, inductance or capacitance
 * at all, so those entries state construction only and let the kit derive the
 * three electrical figures from gauge, material and geometry. Blue Jeans Cable
 * is the exception -- it sells Belden and Canare stock and republishes the
 * datasheet figures -- so those entries carry real published numbers.
 *
 * Power, USB and ethernet entries describe the build and stop there. The engine
 * applies no signal processing to any of them, and nothing in these notes
 * implies otherwise.
 */

import type { CableProfile } from '../../types/hardware';
import { makeCable } from '../cableKit';
import { usd } from '../kit';

export const CABLES_05: CableProfile[] = [
  // =========================================================================
  // Vertere Acoustics -- Great Britain
  // =========================================================================
  makeCable({
    mfr: 'Vertere Acoustics',
    model: 'Redline Interconnect',
    cableType: 'interconnect',
    price: usd(430, 'APPROXIMATE', 2024, 'Approximate price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Braided copper screen tied at the source end',
    dielectric: 'Polyethylene',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#e04b3a',
    tags: ['entry-line', 'hand-assembled'],
    notes:
      'The entry point of the Vertere range: individually insulated high-purity copper conductors laid up as a screened pair, with the screen carried to the source end only. At 1 m the shunt capacitance is a few tens of picofarads, which against any normal line output puts the low-pass corner in the megahertz.',
    provenanceNote:
      'Vertere describes conductor material and connector options but not gauge, conductor count or internal lay. Gauge and geometry here are class estimates for a screened analogue pair, and the electrical figures are derived from them rather than published.',
  }),

  makeCable({
    mfr: 'Vertere Acoustics',
    model: 'Redline Speaker Cable',
    cableType: 'speaker',
    price: usd(780, 'APPROXIMATE', 2024, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#e04b3a',
    tags: ['entry-line', 'hand-assembled'],
    notes:
      'Multiple insulated copper conductors bundled and twisted, terminated in-house. At roughly 13 AWG a 3 m run contributes on the order of 0.04 ohm of loop resistance, which into a 4 ohm minimum is under a tenth of a decibel and tracks the impedance curve.',
    provenanceNote:
      'Gauge is inferred from the physical size of the cable and its conductor count; Vertere does not state an AWG figure or publish resistance, inductance or capacitance. All three electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Vertere Acoustics',
    model: 'Redline Power Cable',
    cableType: 'power',
    price: usd(520, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 13,
    shielding: 'Screened, drain tied at the plug end',
    termination: ['IEC C13', 'IEC C15', 'UK 13 A', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#e04b3a',
    tags: ['entry-line', 'screened'],
    notes:
      'A screened three-core mains cord in roughly 2.5 sq mm per core, rated for the 13 A UK plug fuse, with the connector bodies mechanically damped. The engine applies no signal processing to power cords, so this entry changes the chain description and the price and nothing in the audio.',
    provenanceNote:
      'Conductor cross-section is inferred from the stated current rating and cable diameter; Vertere publishes no AWG or electrical figures. The kit derives R, L and C but the DSP never reads them for a power cord.',
  }),

  makeCable({
    mfr: 'Vertere Acoustics',
    model: 'Redline D-Fi',
    cableType: 'interconnect',
    price: usd(390, 'APPROXIMATE', 2024, 'Approximate price for a 1 m single cable.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    characteristicOhms: 75,
    shielding: 'Braided screen serving as the return conductor',
    dielectric: 'Polyethylene',
    termination: ['RCA', 'BNC'],
    confidence: 'ESTIMATED',
    accent: '#e04b3a',
    tags: ['digital-coax', '75-ohm'],
    notes:
      'A 75 ohm coaxial lead for S/PDIF, sold as a single cable rather than a pair. It is filed as an interconnect because that is the physical construction; what actually governs a self-clocked digital link is impedance match and reflection, which this engine does not model.',
    provenanceNote:
      'Characteristic impedance is the stated design target for the cable. Gauge and per-metre figures are estimates -- Vertere publishes neither -- and no jitter or reflection behaviour is asserted.',
  }),

  makeCable({
    mfr: 'Vertere Acoustics',
    model: 'Pulse-B Interconnect',
    cableType: 'interconnect',
    price: usd(1050, 'APPROXIMATE', 2024, 'Approximate price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Braided screen with a separate drain',
    dielectric: 'Polyethylene over an air-spaced core',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#e04b3a',
    tags: ['pulse-series', 'hand-assembled'],
    notes:
      'The Pulse series moves to a larger, more loosely packed core with more insulation volume per conductor, which lowers capacitance relative to the Redline lead of the same length. The absolute figures either way are small enough that the computed in-band response is flat to two decimal places.',
    provenanceNote:
      'Vertere states the hand-formed multi-conductor construction and the dielectric family but no dimensions. Gauge, geometry class and all three electrical figures are estimated or derived here.',
  }),

  makeCable({
    mfr: 'Vertere Acoustics',
    model: 'Pulse-B Speaker Cable',
    cableType: 'speaker',
    price: usd(1900, 'APPROXIMATE', 2024, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#e04b3a',
    tags: ['pulse-series', 'hand-assembled'],
    notes:
      'A heavier conductor bundle than the Redline cable in the same twisted lay. Around 12 AWG the loop resistance of a 3 m run is roughly 0.03 ohm, which adds to the amplifier output impedance and slightly reduces the damping the driver sees.',
    provenanceNote:
      'Gauge inferred from cable diameter and conductor count; no AWG or per-metre electrical figures are published by Vertere. All electrical values are derived by the kit.',
  }),

  makeCable({
    mfr: 'Vertere Acoustics',
    model: 'Pulse-B Power Cable',
    cableType: 'power',
    price: usd(1150, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    shielding: 'Screened, with damped connector bodies',
    termination: ['IEC C13', 'IEC C15', 'IEC C19', 'UK 13 A', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#e04b3a',
    tags: ['pulse-series', 'screened', 'high-current'],
    notes:
      'A heavier screened mains cord with C19 available for amplifiers that draw large transient currents, and machined connector shells clamped to the cable rather than moulded. Gauge and current rating are real engineering; the engine applies zero DSP to power cords and this entry is no exception.',
    provenanceNote:
      'Cross-section and current rating are inferred from the connector options and cable diameter. No electrical figures are published, and none are read by the engine for this type.',
  }),

  makeCable({
    mfr: 'Vertere Acoustics',
    model: 'Pulse-R Interconnect',
    cableType: 'interconnect',
    price: usd(3200, 'APPROXIMATE', 2024, 'Approximate price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 23,
    shielding: 'Braided screen, source-end tie',
    dielectric: 'Air-spaced polyethylene',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#e04b3a',
    tags: ['reference-line', 'air-spaced'],
    notes:
      'The reference analogue lead: solid conductors held apart by an air-spaced dielectric, which is the geometry that minimises capacitance and, by the same trade, raises inductance. On a 1 m interconnect neither figure is large enough to do anything measurable in band.',
    provenanceNote:
      'Solid-core construction and the air-spaced dielectric are stated by the maker; gauge is an estimate and no per-metre electrical figures are published.',
  }),

  makeCable({
    mfr: 'Vertere Acoustics',
    model: 'Pulse-R Speaker Cable',
    cableType: 'speaker',
    price: usd(6400, 'APPROXIMATE', 2024, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#e04b3a',
    tags: ['reference-line', 'heavy-gauge'],
    notes:
      'A large conductor bundle terminated in machined spades or bananas. At about 11 AWG a 3 m run is near 0.025 ohm of loop resistance -- roughly 0.05 dB into a 4 ohm load, and the only cable property in this category that is large enough to see on a sweep.',
    provenanceNote:
      'Gauge inferred from conductor count and cable diameter. Vertere publishes no resistance, inductance or capacitance for any cable, so all three are derived.',
  }),

  makeCable({
    mfr: 'Vertere Acoustics',
    model: 'HB Series Interconnect',
    cableType: 'interconnect',
    price: usd(9500, 'APPROXIMATE', 2024, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 23,
    shielding: 'Multi-layer screen over a damped core',
    dielectric: 'Air-spaced polyethylene',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#e04b3a',
    tags: ['flagship', 'hand-built', 'air-spaced'],
    notes:
      'The hand-built flagship, distinguished mainly by mechanical work: layered damping around the conductor core, machined connector bodies and individually built-up assemblies. Electrically it sits in the same low-capacitance region as the Pulse-R lead, which on a 1 m analogue run means a computed in-band deviation of essentially zero.',
    provenanceNote:
      'Construction is described qualitatively by the maker in terms of damping and assembly method rather than dimensions. Gauge is estimated and every electrical figure is derived.',
  }),

  makeCable({
    mfr: 'Vertere Acoustics',
    model: 'HB Series Speaker Cable',
    cableType: 'speaker',
    price: usd(19000, 'APPROXIMATE', 2024, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 10,
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#e04b3a',
    tags: ['flagship', 'hand-built', 'heavy-gauge'],
    notes:
      'A very large woven conductor assembly with damping layers and machined terminations. The weave places opposing currents adjacent, which cancels part of the external field and lowers loop inductance relative to a simple twisted bundle, at the cost of higher capacitance -- harmless into a loudspeaker.',
    provenanceNote:
      'Woven construction and heavy gauge are evident from the product and its stated conductor count; the exact AWG is not published, nor are resistance, inductance or capacitance.',
  }),

  // =========================================================================
  // Kubala-Sosna -- United States
  // =========================================================================
  makeCable({
    mfr: 'Kubala-Sosna',
    model: 'Anticipation Interconnect',
    cableType: 'interconnect',
    price: usd(550, 'APPROXIMATE', 2024, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Screened, in the line colour-coded jacket',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#8a6fbf',
    tags: ['entry-line'],
    notes:
      'The entry line, built on the same assembly method as the rest of the range with fewer conductors and a simpler jacket. Kubala-Sosna states almost nothing about what is inside, so this entry records the type, the terminations and the price, and derives the electrical figures from a conventional screened pair.',
    provenanceNote:
      'This maker publishes no conductor gauge, count, dielectric or per-metre electrical data for any line. Conductor material, gauge and geometry here are class estimates for a screened analogue interconnect, not manufacturer statements.',
  }),

  makeCable({
    mfr: 'Kubala-Sosna',
    model: 'Anticipation Speaker Cable',
    cableType: 'speaker',
    price: usd(1000, 'APPROXIMATE', 2024, 'Approximate price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#8a6fbf',
    tags: ['entry-line'],
    notes:
      'A moderately sized multi-conductor bundle in the house jacket, offered single-wire or bi-wire. Around 13 AWG, a 2.5 m run adds roughly 0.03 ohm of loop resistance in series with the driver.',
    provenanceNote:
      'Gauge and geometry are class estimates: Kubala-Sosna publishes no AWG, conductor count or electrical figures. All three per-metre values are derived by the kit.',
  }),

  makeCable({
    mfr: 'Kubala-Sosna',
    model: 'Anticipation Power Cord',
    cableType: 'power',
    price: usd(550, 'APPROXIMATE', 2024, 'Approximate price for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    termination: ['IEC C13', 'IEC C15', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#8a6fbf',
    tags: ['entry-line'],
    notes:
      'A three-core mains cord assembled to the same pattern as the rest of the line and rated for a 15 A North American outlet. No signal processing is applied to power cords by this engine, and no construction detail here is offered as though it were.',
    provenanceNote:
      'Current rating follows from the connector set; gauge is an estimate. The maker publishes no electrical data, and none would be read for this cable type in any case.',
  }),

  makeCable({
    mfr: 'Kubala-Sosna',
    model: 'Persuasion Interconnect',
    cableType: 'interconnect',
    price: usd(900, 'APPROXIMATE', 2024, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Screened',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#8a6fbf',
    tags: ['mid-line'],
    notes:
      'One step above Anticipation in the same family, with a larger conductor assembly and heavier jacket. The difference the engine can compute between the two at 1 m is a fraction of a picofarad-driven decibel, which is to say none.',
    provenanceNote:
      'No construction dimensions are published by the maker. Gauge and geometry are estimates and every electrical figure is derived.',
  }),

  makeCable({
    mfr: 'Kubala-Sosna',
    model: 'Persuasion Speaker Cable',
    cableType: 'speaker',
    price: usd(1700, 'APPROXIMATE', 2024, 'Approximate price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#8a6fbf',
    tags: ['mid-line'],
    notes:
      'A larger conductor bundle than the Anticipation cable in the same lay and jacket family, single-wire or bi-wire. The extra copper is the part with a computable consequence: about 0.026 ohm of loop resistance over 2.5 m rather than 0.033.',
    provenanceNote:
      'Gauge is estimated from the relative position in the range and the cable diameter; no AWG or electrical figures are published.',
  }),

  makeCable({
    mfr: 'Kubala-Sosna',
    model: 'Expression Interconnect',
    cableType: 'interconnect',
    price: usd(1650, 'APPROXIMATE', 2024, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Screened',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#8a6fbf',
    tags: ['mid-line'],
    notes:
      'The middle of the range and the most widely sold of the lines. Heavier conductors and a thicker jacket than Persuasion, terminated with locking XLR or machined RCA bodies.',
    provenanceNote:
      'Kubala-Sosna does not publish conductor gauge, count or dielectric. Gauge and geometry are class estimates and the electrical figures are derived from them.',
  }),

  makeCable({
    mfr: 'Kubala-Sosna',
    model: 'Expression Speaker Cable',
    cableType: 'speaker',
    price: usd(3100, 'APPROXIMATE', 2024, 'Approximate price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#8a6fbf',
    tags: ['mid-line', 'heavy-gauge'],
    notes:
      'A heavy bundle for the middle of the line, available single-wire, bi-wire or with jumpers. At roughly 11 AWG the 2.5 m loop resistance is about 0.021 ohm, low enough that the amplifier output impedance dominates what the driver sees.',
    provenanceNote:
      'Gauge estimated from cable diameter and position in the range; the maker publishes no AWG and no per-metre electrical data.',
  }),

  makeCable({
    mfr: 'Kubala-Sosna',
    model: 'Expression Power Cord',
    cableType: 'power',
    price: usd(1500, 'APPROXIMATE', 2024, 'Approximate price for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 20,
    termination: ['IEC C13', 'IEC C15', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#8a6fbf',
    tags: ['mid-line', 'high-current'],
    notes:
      'Offered with C19 and a 20 A inlet for large amplifiers, which is a real distinction between this cord and the entry one: more copper and a connector set rated to carry it. The audio path is untouched by the engine for this type.',
    provenanceNote:
      'Gauge inferred from the 20 A connector options. No electrical figures published, and none are used by the DSP for power cords.',
  }),

  makeCable({
    mfr: 'Kubala-Sosna',
    model: 'Emotion Interconnect',
    cableType: 'interconnect',
    price: usd(2900, 'APPROXIMATE', 2024, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Screened, heavier jacket than Expression',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#8a6fbf',
    tags: ['upper-line'],
    notes:
      'Upper-line analogue lead, physically larger and stiffer than Expression with the same connector options. Nothing about its measurable behaviour at 1 m separates it from the cheaper leads in the range, and this database will not pretend otherwise.',
    provenanceNote:
      'No published construction dimensions or electrical figures. Gauge and geometry are estimates; all per-metre values are derived.',
  }),

  makeCable({
    mfr: 'Kubala-Sosna',
    model: 'Emotion Speaker Cable',
    cableType: 'speaker',
    price: usd(5600, 'APPROXIMATE', 2024, 'Approximate price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 10,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#8a6fbf',
    tags: ['upper-line', 'heavy-gauge'],
    notes:
      'A very heavy conductor bundle, single-wire or bi-wire, on machined terminations. About 0.016 ohm of loop resistance over 2.5 m -- the point of diminishing returns for gauge, since the amplifier and the connector interfaces contribute comparable amounts.',
    provenanceNote:
      'Gauge estimated from cable diameter; not published. Resistance, inductance and capacitance are all derived by the kit.',
  }),

  makeCable({
    mfr: 'Kubala-Sosna',
    model: 'Elation Power Cord',
    cableType: 'power',
    price: usd(5000, 'APPROXIMATE', 2024, 'Approximate price for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 20,
    termination: ['IEC C15', 'IEC C19', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#8a6fbf',
    tags: ['flagship-line', 'high-current'],
    notes:
      'A very heavy mains cord with 20 A terminations, built to the same pattern as the rest of the line. It is a substantial piece of cable assembly and the engine applies exactly zero signal processing to it -- the chain description and the total price change, the audio does not.',
    provenanceNote:
      'Gauge inferred from the connector rating and cable diameter; no figures are published. Power cords carry no DSP contribution in this engine by design.',
  }),

  makeCable({
    mfr: 'Kubala-Sosna',
    model: 'Realization Speaker Cable',
    cableType: 'speaker',
    price: usd(19000, 'APPROXIMATE', 2024, 'Approximate price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 9,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#8a6fbf',
    tags: ['flagship', 'heavy-gauge'],
    notes:
      'The flagship: the largest conductor assembly in the range on machined hardware. Loop resistance over 2.5 m works out near 0.013 ohm, about 0.003 ohm better than the Emotion cable, a difference no loudspeaker load can resolve.',
    provenanceNote:
      'Gauge is an estimate from cable diameter and conductor count; the maker publishes no AWG and no electrical data for any line, including this one.',
  }),

  // =========================================================================
  // JPS Labs -- United States
  // =========================================================================
  makeCable({
    mfr: 'JPS Labs',
    model: 'UltraConductor 2 Interconnect',
    cableType: 'interconnect',
    price: usd(200, 'APPROXIMATE', 2024, 'Approximate price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Foil and drain over the twisted pair',
    dielectric: 'Polyethylene',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#7f8c9b',
    tags: ['entry-line'],
    notes:
      'The entry line, a shielded twisted pair in the maker\'s copper alloy with moulded strain relief. Capacitance at 1 m is a few tens of picofarads, which is below the level at which any normal source impedance produces an in-band effect.',
    provenanceNote:
      'JPS describes its conductors as an aluminium-bearing copper alloy but publishes no resistivity for it, so the kit models plain copper and the derived resistance should be read as approximate. Gauge and geometry are class estimates.',
  }),

  makeCable({
    mfr: 'JPS Labs',
    model: 'UltraConductor 2 Speaker Cable',
    cableType: 'speaker',
    price: usd(520, 'APPROXIMATE', 2024, 'Approximate price for a 2.4 m terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#7f8c9b',
    tags: ['entry-line'],
    notes:
      'Multiple insulated conductors twisted under a moulded jacket, terminated with soldered spades or bananas. Roughly 0.025 ohm of loop resistance over a 2.4 m run, which is a small addition to the source impedance the driver sees.',
    provenanceNote:
      'Conductor is JPS alloy rather than plain OFC; resistivity for it is not published, so copper is used for the derivation. Gauge is estimated from cable diameter.',
  }),

  makeCable({
    mfr: 'JPS Labs',
    model: 'Superconductor Petite',
    cableType: 'interconnect',
    price: usd(400, 'APPROXIMATE', 2024, 'Approximate price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 23,
    shielding: 'Full-coverage shield with drain',
    dielectric: 'Low-loss polymer',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#7f8c9b',
    tags: ['superconductor-series', 'slim'],
    notes:
      'A deliberately slim member of the Superconductor family, built for tight equipment racks where a stiff cable strains the socket. Small conductors and a thin dielectric give it slightly higher capacitance per metre than the larger leads in the line.',
    provenanceNote:
      'Slim construction and shielding are stated; gauge is estimated. JPS alloy conductors are modelled as copper because the alloy resistivity is not published.',
  }),

  makeCable({
    mfr: 'JPS Labs',
    model: 'Superconductor 3',
    cableType: 'interconnect',
    price: usd(750, 'APPROXIMATE', 2024, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Full-coverage shield with drain',
    dielectric: 'Low-loss polymer',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#7f8c9b',
    tags: ['superconductor-series'],
    notes:
      'The standard Superconductor analogue lead: larger conductors than the Petite in the same shielded twisted layout, available single-ended or balanced. Both variants are electrically unremarkable at 1 m, which is the expected and correct result for an interconnect.',
    provenanceNote:
      'Gauge estimated from cable diameter; JPS publishes no AWG or per-metre figures. The alloy conductor is modelled as copper.',
  }),

  makeCable({
    mfr: 'JPS Labs',
    model: 'Superconductor Q',
    cableType: 'speaker',
    price: usd(1900, 'APPROXIMATE', 2024, 'Approximate price for a 2.4 m terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 11,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#7f8c9b',
    tags: ['superconductor-series', 'flat-profile'],
    notes:
      'A wide, flat conductor arrangement rather than a round bundle. Bringing the two polarities close together across a broad face is the classic low-inductance, high-capacitance trade -- fine into a loudspeaker, and the reason this geometry never appears on an interconnect.',
    provenanceNote:
      'Flat profile and heavy gauge are evident from the product; the exact AWG is not published and the alloy conductor is modelled as copper. All three electrical figures are derived from gauge and geometry.',
  }),

  makeCable({
    mfr: 'JPS Labs',
    model: 'Aluminata Interconnect',
    cableType: 'interconnect',
    price: usd(3400, 'APPROXIMATE', 2024, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Multi-layer shield with heavy jacket',
    dielectric: 'Low-loss polymer',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#7f8c9b',
    tags: ['flagship', 'alloy-conductor'],
    notes:
      'The flagship analogue lead, named for the aluminium-bearing conductor alloy JPS uses throughout the range and applies most heavily here. Construction is a heavily shielded and jacketed twisted pair; the measurable per-metre properties remain those of a good shielded interconnect.',
    provenanceNote:
      'JPS states the alloy conductor and the multi-layer shielding but publishes no gauge or per-metre electrical figures. Copper resistivity is used for the derivation because the alloy figure is not public.',
  }),

  makeCable({
    mfr: 'JPS Labs',
    model: 'Aluminata Speaker Cable',
    cableType: 'speaker',
    price: usd(8500, 'APPROXIMATE', 2024, 'Approximate price for a 2.4 m terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 10,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#7f8c9b',
    tags: ['flagship', 'flat-profile', 'heavy-gauge'],
    notes:
      'The largest flat conductor assembly in the range, terminated with heavy spades. Around 10 AWG a 2.4 m run holds loop resistance near 0.016 ohm, and the flat geometry keeps series inductance low, which is the pair of numbers that actually reaches the DSP.',
    provenanceNote:
      'Gauge is estimated from the physical cable; JPS publishes no AWG or electrical data. The aluminium-bearing alloy is modelled with copper resistivity.',
  }),

  makeCable({
    mfr: 'JPS Labs',
    model: 'Aluminata Power AC',
    cableType: 'power',
    price: usd(3200, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 20,
    shielding: 'Shielded, heavy jacket',
    termination: ['IEC C15', 'IEC C19', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#7f8c9b',
    tags: ['flagship', 'high-current', 'shielded'],
    notes:
      'A very heavy shielded mains cord with 20 A terminations, matching the flagship analogue cables in build. This is a well-made piece of power wiring and the engine applies no signal processing to it whatsoever.',
    provenanceNote:
      'Gauge inferred from the 20 A rating and cable diameter. No electrical figures are published and none are read for this type.',
  }),

  makeCable({
    mfr: 'JPS Labs',
    model: 'Kaptovator AC',
    cableType: 'power',
    price: usd(900, 'APPROXIMATE', 2024, 'Approximate price for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 20,
    shielding: 'Shielded',
    dielectric: 'Polyimide film',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#7f8c9b',
    tags: ['polyimide-insulated', 'high-current'],
    notes:
      'The long-running JPS mains cord, insulated with polyimide film -- a material chosen for temperature and dielectric strength rather than for any claimed audio property -- and offered with C19 for high-draw amplifiers. Power cords receive no DSP in this engine.',
    provenanceNote:
      'The polyimide insulation is a stated construction fact. Gauge is inferred from the connector rating; no per-metre electrical figures exist for this cord and none are used.',
  }),

  makeCable({
    mfr: 'JPS Labs',
    model: 'The Digital AC',
    cableType: 'power',
    price: usd(600, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 15,
    shielding: 'Shielded',
    termination: ['IEC C13', 'IEC C7', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#7f8c9b',
    tags: ['low-current', 'shielded'],
    notes:
      'A lighter shielded cord intended for source components, which draw a fraction of the current an amplifier does, so the gauge is smaller and the cable more flexible. That is the whole of the difference; no signal-path behaviour is claimed or modelled.',
    provenanceNote:
      'Gauge inferred from the intended low-current application and cable diameter. No electrical figures published or applied.',
  }),

  makeCable({
    mfr: 'JPS Labs',
    model: 'The Power AC+',
    cableType: 'power',
    price: usd(1500, 'APPROXIMATE', 2024, 'Approximate price for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 20,
    shielding: 'Shielded',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#7f8c9b',
    tags: ['high-current', 'shielded'],
    notes:
      'The mid-range amplifier cord: heavier conductors than the Digital AC and the same 20 A connector options as the flagship. Gauge and rating are genuine electrical engineering that matters for delivering current to a rectifier; none of it reaches the audio path in this engine.',
    provenanceNote:
      'Gauge inferred from the current rating and cable size; JPS publishes no cross-section or per-metre data.',
  }),

  // =========================================================================
  // Harmonic Technology -- United States
  // =========================================================================
  makeCable({
    mfr: 'Harmonic Technology',
    model: 'Truthlink',
    cableType: 'interconnect',
    price: usd(160, 'APPROXIMATE', 2024, 'Approximate price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Braided copper shield',
    dielectric: 'Teflon (PTFE)',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d4a15a',
    tags: ['single-crystal-copper', 'ptfe', 'entry-line'],
    notes:
      'The cable that made the brand: ohno continuous-cast copper conductors in a PTFE dielectric, shielded and twisted. PTFE has a lower dielectric constant than the polyethylene used in cheaper leads, which lowers capacitance per metre for the same spacing -- a real if small electrical consequence.',
    provenanceNote:
      'Conductor casting process and PTFE dielectric are manufacturer statements. Gauge is estimated and no per-metre resistance, inductance or capacitance is published, so all three are derived.',
  }),

  makeCable({
    mfr: 'Harmonic Technology',
    model: 'Truthlink Silver',
    cableType: 'interconnect',
    price: usd(340, 'APPROXIMATE', 2024, 'Approximate price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Braided shield',
    dielectric: 'Teflon (PTFE)',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d4a15a',
    tags: ['silver-conductor', 'ptfe'],
    notes:
      'The Truthlink layout with silver conductors in place of copper. Silver is about eight per cent less resistive than copper for the same cross-section, so a 1 m interconnect carrying microamps into a high-impedance input sees a difference far below anything the engine can report.',
    provenanceNote:
      'Silver conductor and PTFE dielectric are manufacturer statements; gauge is estimated and the electrical figures are derived using silver resistivity.',
  }),

  makeCable({
    mfr: 'Harmonic Technology',
    model: 'Magic Link Two',
    cableType: 'interconnect',
    price: usd(600, 'APPROXIMATE', 2024, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Braided shield with drain',
    dielectric: 'Teflon (PTFE) over an air-spaced core',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d4a15a',
    tags: ['single-crystal-copper', 'air-spaced'],
    notes:
      'Larger single-crystal copper conductors held in an air-spaced PTFE structure. Adding air to the dielectric lowers the effective permittivity further and so lowers capacitance per metre; the same spacing raises inductance slightly, which is the trade the geometry table encodes.',
    provenanceNote:
      'Conductor type and dielectric construction are stated by the maker. Gauge is estimated; no per-metre figures are published.',
  }),

  makeCable({
    mfr: 'Harmonic Technology',
    model: 'Pro-Silway III',
    cableType: 'interconnect',
    price: usd(800, 'APPROXIMATE', 2024, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Braided shield',
    dielectric: 'Teflon (PTFE)',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#d4a15a',
    tags: ['hybrid-conductor', 'ptfe'],
    notes:
      'The long-running Pro-Silway analogue lead, using silver and copper conductors together in a PTFE-insulated shielded pair. A mixed conductor set lands between the two metals in resistivity, which for a line-level run is a difference of no consequence at any length a listener would use.',
    provenanceNote:
      'The hybrid conductor is described by the maker but the copper-to-silver proportion is not stated, so the resistivity used here is the mid-point value from the kit. Gauge is estimated.',
  }),

  makeCable({
    mfr: 'Harmonic Technology',
    model: 'Cyber Link Copper',
    cableType: 'interconnect',
    price: usd(220, 'APPROXIMATE', 2024, 'Approximate price for a 1 m single cable.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'coaxial',
    gauge: 21,
    characteristicOhms: 75,
    shielding: 'Braided shield used as the return path',
    dielectric: 'Teflon (PTFE)',
    termination: ['RCA', 'BNC'],
    confidence: 'ESTIMATED',
    accent: '#d4a15a',
    tags: ['digital-coax', '75-ohm'],
    notes:
      'A 75 ohm coaxial S/PDIF lead in single-crystal copper with a PTFE dielectric, sold singly. Recorded as an interconnect because that is its construction; what governs a digital coax link is impedance match and reflections, which this engine does not model and does not claim to.',
    provenanceNote:
      'Characteristic impedance is the stated design figure. Gauge is estimated and no per-metre electrical data is published.',
  }),

  makeCable({
    mfr: 'Harmonic Technology',
    model: 'Melody',
    cableType: 'speaker',
    price: usd(650, 'APPROXIMATE', 2024, 'Approximate price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    dielectric: 'Teflon (PTFE)',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#d4a15a',
    tags: ['single-crystal-copper', 'entry-line'],
    notes:
      'The entry speaker cable in the range: single-crystal copper conductors in PTFE, twisted and jacketed. About 0.026 ohm of loop resistance over a 2.5 m run, which sits in series with the amplifier output impedance ahead of the driver.',
    provenanceNote:
      'Conductor and dielectric are manufacturer statements; gauge is estimated from the cable and no electrical figures are published.',
  }),

  makeCable({
    mfr: 'Harmonic Technology',
    model: 'Pro-9 Plus',
    cableType: 'speaker',
    price: usd(950, 'APPROXIMATE', 2024, 'Approximate price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    dielectric: 'Teflon (PTFE)',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#d4a15a',
    tags: ['single-crystal-copper', 'heavy-gauge'],
    notes:
      'A heavy-gauge single-crystal copper cable, the mainstay of the line, available single-wire or bi-wire. Near 10 AWG the 2.5 m loop resistance falls to about 0.016 ohm, roughly a third of what a 16 AWG lead of the same length would add.',
    provenanceNote:
      'Gauge is inferred from the model designation and the cable diameter rather than a published AWG statement, and the derived resistance should be read as approximate.',
  }),

  makeCable({
    mfr: 'Harmonic Technology',
    model: 'Pro-11 Reference',
    cableType: 'speaker',
    price: usd(1700, 'APPROXIMATE', 2024, 'Approximate price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'braided',
    gauge: 11,
    dielectric: 'Teflon (PTFE)',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#d4a15a',
    tags: ['single-crystal-copper', 'woven'],
    notes:
      'The reference speaker cable, with the conductors woven rather than simply twisted. A weave that alternates polarity between adjacent runs cancels part of the external field and drops loop inductance below that of a twisted bundle, with correspondingly higher capacitance.',
    provenanceNote:
      'Woven construction and conductor type are manufacturer statements; gauge is estimated from the model designation and cable size. No per-metre figures are published.',
  }),

  makeCable({
    mfr: 'Harmonic Technology',
    model: 'AC-11 Magic',
    cableType: 'power',
    price: usd(450, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 15,
    shielding: 'Shielded',
    dielectric: 'Teflon (PTFE)',
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#d4a15a',
    tags: ['single-crystal-copper', 'shielded'],
    notes:
      'A shielded three-core mains cord using the same single-crystal copper and PTFE insulation as the signal cables. The materials are as described; the engine applies no processing to power cords, so the audio is bit-identical whichever cord is selected.',
    provenanceNote:
      'Gauge inferred from the model designation and current rating. No electrical figures published, and none would be applied for this type.',
  }),

  makeCable({
    mfr: 'Harmonic Technology',
    model: 'Fantasy AC-10',
    cableType: 'power',
    price: usd(900, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 20,
    shielding: 'Shielded, heavy jacket',
    dielectric: 'Teflon (PTFE)',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#d4a15a',
    tags: ['flagship-line', 'high-current', 'shielded'],
    notes:
      'The heavier mains cord in the range, around 10 AWG per core with 20 A connector options for power amplifiers. Larger conductors reduce the voltage drop under a current transient at the wall, which is the honest reason to buy gauge; no DSP is applied here.',
    provenanceNote:
      'Gauge inferred from the model designation and the 20 A connector options rather than a published cross-section.',
  }),

  makeCable({
    mfr: 'Harmonic Technology',
    model: 'Platinum Link USB',
    cableType: 'usb',
    price: usd(280, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil and braid over separated power and data pairs',
    dielectric: 'Teflon (PTFE)',
    termination: ['USB A to B'],
    confidence: 'ESTIMATED',
    accent: '#d4a15a',
    tags: ['usb-2.0', 'shielded'],
    notes:
      'A USB 2.0 assembly in single-crystal copper with the power and data pairs separately jacketed inside a common shield. It is a compliant 90 ohm data cable: the engine applies no signal processing to USB, since the link either carries the packets or fails to.',
    provenanceNote:
      'Conductor material and the separated-pair construction are manufacturer statements; the 90 ohm figure is the USB 2.0 requirement rather than a measured value for this assembly.',
  }),

  // =========================================================================
  // Acoustic Zen -- United States
  // =========================================================================
  makeCable({
    mfr: 'Acoustic Zen',
    model: 'WOW',
    cableType: 'interconnect',
    price: usd(320, 'APPROXIMATE', 2024, 'Approximate price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Braided shield',
    dielectric: 'Teflon (PTFE)',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2e9e8f',
    tags: ['zero-crystal-copper', 'entry-line'],
    notes:
      'The entry interconnect, using the continuous-cast copper the maker calls zero-crystal, insulated in PTFE and shielded. Conductors of graduated diameter are used through the range, which spreads skin-effect behaviour across frequency; at line level over 1 m the audible consequence is nil and the engine reports it as such.',
    provenanceNote:
      'Conductor casting process, dielectric and shield are manufacturer statements. Gauge is estimated and Acoustic Zen publishes no resistance, inductance or capacitance, so all three are derived.',
  }),

  makeCable({
    mfr: 'Acoustic Zen',
    model: 'Matrix Reference II',
    cableType: 'interconnect',
    price: usd(750, 'APPROXIMATE', 2024, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Braided shield with drain',
    dielectric: 'Teflon (PTFE) with air spacing',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2e9e8f',
    tags: ['zero-crystal-copper', 'air-spaced'],
    notes:
      'A larger multi-conductor copper lead with air spacing inside the PTFE structure. Air lowers the effective dielectric constant, so capacitance per metre falls relative to a solidly filled cable of the same spacing.',
    provenanceNote:
      'Conductor and dielectric construction are stated by the maker; gauge is estimated and no per-metre electrical figures are published.',
  }),

  makeCable({
    mfr: 'Acoustic Zen',
    model: 'Silver Reference II',
    cableType: 'interconnect',
    price: usd(1300, 'APPROXIMATE', 2024, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Braided shield with drain',
    dielectric: 'Teflon (PTFE) with air spacing',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2e9e8f',
    tags: ['silver-conductor', 'air-spaced'],
    notes:
      'The Matrix construction with silver conductors. Silver has the lowest resistivity of any metal used in cable, about eight per cent below copper, which over a 1 m interconnect amounts to a few milliohms in a circuit where the load is measured in kilohms.',
    provenanceNote:
      'Silver conductor and air-spaced PTFE are manufacturer statements; gauge is estimated. Derivation uses silver resistivity and geometry norms because no figures are published.',
  }),

  makeCable({
    mfr: 'Acoustic Zen',
    model: 'Absolute Copper',
    cableType: 'interconnect',
    price: usd(1900, 'APPROXIMATE', 2024, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'solid-core',
    gauge: 22,
    shielding: 'Multi-layer shield',
    dielectric: 'Teflon (PTFE) with air spacing',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#2e9e8f',
    tags: ['flagship-line', 'solid-conductor'],
    notes:
      'The top copper interconnect in the range, moving to solid conductors held apart in an air-spaced PTFE dielectric. That geometry gives the lowest capacitance and the highest inductance of any layout here, which for a 1 m line-level run is the best trade available and still amounts to no in-band change.',
    provenanceNote:
      'Solid-conductor and air-spaced construction are described by the maker; conductor count and gauge are estimated and no electrical figures are published.',
  }),

  makeCable({
    mfr: 'Acoustic Zen',
    model: 'MC2',
    cableType: 'interconnect',
    price: usd(400, 'APPROXIMATE', 2024, 'Approximate price for a 1 m single cable.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'coaxial',
    gauge: 21,
    characteristicOhms: 75,
    shielding: 'Braided shield forming the return path',
    dielectric: 'Teflon (PTFE)',
    termination: ['RCA', 'BNC'],
    confidence: 'ESTIMATED',
    accent: '#2e9e8f',
    tags: ['digital-coax', '75-ohm'],
    notes:
      'A 75 ohm coaxial digital lead in zero-crystal copper, sold as a single cable. Filed here as an interconnect because that is the construction; the properties that matter for a self-clocked S/PDIF link are impedance uniformity and reflection, neither of which this engine models.',
    provenanceNote:
      'The 75 ohm figure is the stated design impedance for S/PDIF use. Gauge is estimated; no per-metre electrical data is published.',
  }),

  makeCable({
    mfr: 'Acoustic Zen',
    model: 'Satori Shotgun',
    cableType: 'speaker',
    price: usd(1400, 'APPROXIMATE', 2024, 'Approximate price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'shotgun',
    gauge: 11,
    dielectric: 'Teflon (PTFE)',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2e9e8f',
    tags: ['zero-crystal-copper', 'double-run'],
    notes:
      'The shotgun version runs a separate cable for each polarity instead of sharing one jacket. Two independent runs halve the effective loop resistance compared with a single cable of the same conductor set, taking a 2.5 m run to roughly 0.01 ohm; the wider spacing between polarities raises inductance a little in exchange.',
    provenanceNote:
      'Double-run construction and conductor type are manufacturer statements; gauge is estimated per run and no per-metre figures are published.',
  }),

  makeCable({
    mfr: 'Acoustic Zen',
    model: 'Hologram II',
    cableType: 'speaker',
    price: usd(2400, 'APPROXIMATE', 2024, 'Approximate price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    dielectric: 'Teflon (PTFE)',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2e9e8f',
    tags: ['zero-crystal-copper', 'heavy-gauge'],
    notes:
      'A large multi-conductor cable built from copper strands of several different diameters in a twisted lay. Loop resistance over 2.5 m lands near 0.016 ohm, which is the number the engine actually puts in series with the loudspeaker.',
    provenanceNote:
      'Graduated-diameter conductor construction is a manufacturer statement; the aggregate gauge is estimated from the cable and no electrical figures are published.',
  }),

  makeCable({
    mfr: 'Acoustic Zen',
    model: 'Double Barrel Shotgun',
    cableType: 'speaker',
    price: usd(3800, 'APPROXIMATE', 2024, 'Approximate price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'shotgun',
    gauge: 9,
    dielectric: 'Teflon (PTFE)',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#2e9e8f',
    tags: ['double-run', 'heavy-gauge'],
    notes:
      'Two Hologram-grade runs per channel, one per polarity, joined only at the terminations. Paralleling that much copper puts loop resistance for a 2.5 m run under 0.01 ohm, at which point the amplifier output impedance and the connector interfaces dominate what the driver sees.',
    provenanceNote:
      'Effective gauge is estimated from the doubled conductor set rather than published. No resistance, inductance or capacitance figures are issued by the maker.',
  }),

  makeCable({
    mfr: 'Acoustic Zen',
    model: 'Absolute Speaker Cable',
    cableType: 'speaker',
    price: usd(5600, 'APPROXIMATE', 2024, 'Approximate price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'shotgun',
    gauge: 9,
    dielectric: 'Teflon (PTFE) with air spacing',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#2e9e8f',
    tags: ['flagship', 'double-run', 'air-spaced'],
    notes:
      'The flagship speaker cable: a very large conductor set per polarity in an air-spaced PTFE structure, run shotgun. Beyond this gauge, further copper changes the computed series resistance by amounts a loudspeaker impedance curve swamps many times over.',
    provenanceNote:
      'Gauge and geometry are estimates from the physical cable and the stated construction; Acoustic Zen publishes no per-metre electrical data for any model.',
  }),

  makeCable({
    mfr: 'Acoustic Zen',
    model: 'El Nino',
    cableType: 'power',
    price: usd(280, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 15,
    shielding: 'Shielded',
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#2e9e8f',
    tags: ['entry-line', 'shielded'],
    notes:
      'The entry mains cord in the range, a shielded three-core assembly sized for source components. Construction is the whole of what is claimed here: this engine applies no signal processing to power cords.',
    provenanceNote:
      'Gauge is inferred from the intended low-current application; no cross-section or electrical figures are published, and none are read for this type.',
  }),

  makeCable({
    mfr: 'Acoustic Zen',
    model: 'Tsunami II',
    cableType: 'power',
    price: usd(500, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 15,
    shielding: 'Shielded',
    termination: ['IEC C13', 'IEC C15', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#2e9e8f',
    tags: ['shielded'],
    notes:
      'The long-running mid-range mains cord, heavier than the El Nino and built from the same continuous-cast copper as the signal cables. Gauge and shielding are real; no audio-path behaviour is attributed to either.',
    provenanceNote:
      'Cross-section inferred from cable diameter and current rating. Power cords carry no DSP contribution in this engine.',
  }),

  makeCable({
    mfr: 'Acoustic Zen',
    model: 'Gargantua II',
    cableType: 'power',
    price: usd(1600, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 20,
    shielding: 'Shielded, heavy jacket',
    termination: ['IEC C15', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#2e9e8f',
    tags: ['high-current', 'shielded'],
    notes:
      'The heavy amplifier cord in the line, around 10 AWG per core with C19 and 20 A options. A large conductor cross-section limits voltage drop when a power supply pulls a current transient, which is a genuine and unglamorous reason for the gauge; the engine still applies zero processing.',
    provenanceNote:
      'Gauge inferred from the connector rating and cable size rather than a published cross-section.',
  }),

  // =========================================================================
  // Morrow Audio -- United States
  // =========================================================================
  makeCable({
    mfr: 'Morrow Audio',
    model: 'MA1',
    cableType: 'interconnect',
    price: usd(130, 'APPROXIMATE', 2024, 'Approximate price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 24,
    shielding: 'Shielded, drain at the source end',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4a7fd4',
    tags: ['solid-core', 'entry-line', 'direct-sale'],
    notes:
      'The entry interconnect in a range whose models differ mainly by how many individually insulated solid-core copper wires are bundled -- this one uses the fewest. Solid conductors spaced by their own insulation give the low-capacitance, higher-inductance end of the trade, which is the right side for an interconnect.',
    provenanceNote:
      'Solid-core construction and the conductor-count hierarchy are manufacturer statements; the per-wire gauge is estimated and no per-metre electrical figures are published.',
  }),

  makeCable({
    mfr: 'Morrow Audio',
    model: 'MA2',
    cableType: 'interconnect',
    price: usd(190, 'APPROXIMATE', 2024, 'Approximate price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 23,
    shielding: 'Shielded, drain at the source end',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4a7fd4',
    tags: ['solid-core', 'direct-sale'],
    notes:
      'One step up the ladder: more solid-core wires per leg, so a larger effective cross-section and slightly lower series resistance than the MA1. For a line-level interconnect this changes nothing the engine can report, and the entry says so rather than dressing it up.',
    provenanceNote:
      'Effective gauge is estimated from the stated conductor count; Morrow publishes counts but not AWG or per-metre electrical data.',
  }),

  makeCable({
    mfr: 'Morrow Audio',
    model: 'MA3',
    cableType: 'interconnect',
    price: usd(280, 'APPROXIMATE', 2024, 'Approximate price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 22,
    shielding: 'Shielded, drain at the source end',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4a7fd4',
    tags: ['solid-core', 'direct-sale'],
    notes:
      'The middle of the interconnect range, with a further increase in conductor count over the MA2 and the same shielded solid-core construction throughout.',
    provenanceNote:
      'Effective gauge estimated from conductor count. No resistance, inductance or capacitance is published for this line.',
  }),

  makeCable({
    mfr: 'Morrow Audio',
    model: 'MA4',
    cableType: 'interconnect',
    price: usd(460, 'APPROXIMATE', 2024, 'Approximate price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 21,
    shielding: 'Shielded, drain at the source end',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4a7fd4',
    tags: ['solid-core', 'direct-sale'],
    notes:
      'An upper-middle lead with a substantially larger bundle of solid-core wires. The cable is stiffer and heavier than the lower models, which matters for socket strain more than for anything electrical at this signal level.',
    provenanceNote:
      'Effective gauge estimated from the stated conductor count; no per-metre figures are published by the maker.',
  }),

  makeCable({
    mfr: 'Morrow Audio',
    model: 'MA6',
    cableType: 'interconnect',
    price: usd(950, 'APPROXIMATE', 2024, 'Approximate price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 20,
    shielding: 'Shielded, drain at the source end',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4a7fd4',
    tags: ['solid-core', 'upper-line', 'direct-sale'],
    notes:
      'Near the top of the standard interconnect range, with the largest conductor count before the Elite models. Same solid-core, individually insulated construction as the MA1 at a fraction of the price -- the ladder is one variable, repeated.',
    provenanceNote:
      'Effective gauge estimated from the conductor count. Morrow states counts and construction but publishes no electrical figures.',
  }),

  makeCable({
    mfr: 'Morrow Audio',
    model: 'Elite Grand Reference Interconnect',
    cableType: 'interconnect',
    price: usd(3000, 'APPROXIMATE', 2024, 'Approximate price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 19,
    shielding: 'Multi-layer shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#4a7fd4',
    tags: ['flagship', 'solid-core', 'direct-sale'],
    notes:
      'The flagship interconnect: the largest solid-core bundle in the range with heavier shielding and connectors. Its electrical position is the same as the rest of the line -- low capacitance, moderate inductance -- with more copper behind it.',
    provenanceNote:
      'Conductor count for the Elite line is not stated as precisely as for the numbered models, so both gauge and geometry detail here are estimates. No electrical figures are published.',
  }),

  makeCable({
    mfr: 'Morrow Audio',
    model: 'SP1',
    cableType: 'speaker',
    price: usd(260, 'APPROXIMATE', 2024, 'Approximate price for a 2.4 m terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 15,
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4a7fd4',
    tags: ['solid-core', 'entry-line', 'direct-sale'],
    notes:
      'The entry speaker cable, built from the same individually insulated solid-core wires as the interconnects with a count sized for loudspeaker current. At an effective 15 AWG a 2.4 m run adds roughly 0.05 ohm of loop resistance, which into a 4 ohm minimum is about a tenth of a decibel and varies with the impedance curve.',
    provenanceNote:
      'Effective gauge is estimated from the stated conductor count; the maker publishes counts rather than AWG, and no per-metre electrical figures.',
  }),

  makeCable({
    mfr: 'Morrow Audio',
    model: 'SP2',
    cableType: 'speaker',
    price: usd(420, 'APPROXIMATE', 2024, 'Approximate price for a 2.4 m terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 13,
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4a7fd4',
    tags: ['solid-core', 'direct-sale'],
    notes:
      'Twice the conductor count of the SP1 in the same construction, which halves the loop resistance to roughly 0.03 ohm over 2.4 m. This is the one axis on which a speaker cable reliably improves, and it is a gauge question rather than a materials one.',
    provenanceNote:
      'Effective gauge estimated from the stated conductor count rather than published as AWG. No electrical figures are issued.',
  }),

  makeCable({
    mfr: 'Morrow Audio',
    model: 'SP4',
    cableType: 'speaker',
    price: usd(850, 'APPROXIMATE', 2024, 'Approximate price for a 2.4 m terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 12,
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4a7fd4',
    tags: ['solid-core', 'direct-sale'],
    notes:
      'A large bundle of solid-core wires giving an effective 12 AWG or so, for about 0.025 ohm of loop resistance over 2.4 m. Available single-wire or bi-wire, terminated in-house.',
    provenanceNote:
      'Effective gauge estimated from the conductor count; not published as an AWG figure, and no per-metre electrical data exists.',
  }),

  makeCable({
    mfr: 'Morrow Audio',
    model: 'SP6',
    cableType: 'speaker',
    price: usd(1700, 'APPROXIMATE', 2024, 'Approximate price for a 2.4 m terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 11,
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4a7fd4',
    tags: ['solid-core', 'upper-line', 'direct-sale'],
    notes:
      'The upper standard speaker cable, with the highest conductor count short of the Elite models. Around 0.02 ohm of loop resistance over 2.4 m, at which point the amplifier and the terminations contribute as much as the cable.',
    provenanceNote:
      'Effective gauge estimated from conductor count. Morrow publishes construction and counts but not resistance, inductance or capacitance.',
  }),

  makeCable({
    mfr: 'Morrow Audio',
    model: 'MAP1',
    cableType: 'power',
    price: usd(160, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 15,
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#4a7fd4',
    tags: ['entry-line', 'direct-sale'],
    notes:
      'The entry mains cord, sold direct like the rest of the range. A conventional three-core assembly with the maker\'s own connectors; the engine applies no processing to power cords, so this entry contributes build description and price only.',
    provenanceNote:
      'Gauge estimated from the current rating and cable size; no cross-section or electrical figures are published, and none are used for this type.',
  }),

  makeCable({
    mfr: 'Morrow Audio',
    model: 'MAP3',
    cableType: 'power',
    price: usd(480, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 15,
    shielding: 'Shielded',
    termination: ['IEC C13', 'IEC C15', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#4a7fd4',
    tags: ['shielded', 'direct-sale'],
    notes:
      'A heavier shielded cord positioned for amplifiers rather than sources. Larger conductors and a shield are the differences from the MAP1; neither is claimed to alter the audio, and the engine does not model power cords at all.',
    provenanceNote:
      'Gauge inferred from cable diameter and the intended application. No published electrical figures.',
  }),

  // =========================================================================
  // Signal Cable -- United States
  // =========================================================================
  makeCable({
    mfr: 'Signal Cable',
    model: 'Analog One',
    cableType: 'interconnect',
    price: usd(45, 'CURRENT_RETAIL', 2024, 'Direct-sale price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 24,
    shielding: 'Braided copper shield used as the return',
    dielectric: 'Polyethylene',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#6b8f3a',
    tags: ['entry-line', 'direct-sale', 'budget'],
    notes:
      'The cheapest lead in a direct-sale range with no dealer margin in the price. A shielded single-ended coax with soldered RCA bodies; at 1 m its capacitance is small enough that no normal source impedance produces an in-band effect, which is the same answer the costliest interconnect in this database gets.',
    provenanceNote:
      'Signal Cable states conductor material and connector type but not gauge or per-metre electrical figures, so gauge is a class estimate and R, L and C are derived.',
  }),

  makeCable({
    mfr: 'Signal Cable',
    model: 'Analog Two',
    cableType: 'interconnect',
    price: usd(80, 'CURRENT_RETAIL', 2024, 'Direct-sale price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided copper shield used as the return',
    dielectric: 'Teflon (PTFE)',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#6b8f3a',
    tags: ['ptfe', 'direct-sale', 'budget'],
    notes:
      'The standard analogue lead: heavier oxygen-free copper conductors in a PTFE dielectric, with a twisted-pair variant for the balanced version. PTFE lowers capacitance per metre relative to the polyethylene used in the Analog One.',
    provenanceNote:
      'Conductor material and dielectric are stated; gauge is estimated and no per-metre electrical data is published.',
  }),

  makeCable({
    mfr: 'Signal Cable',
    model: 'Silver Resolution Interconnect',
    cableType: 'interconnect',
    price: usd(230, 'CURRENT_RETAIL', 2024, 'Direct-sale price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided shield',
    dielectric: 'Teflon (PTFE)',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#6b8f3a',
    tags: ['silver-conductor', 'ptfe', 'direct-sale'],
    notes:
      'Silver conductors in PTFE, the top analogue lead in the range and still cheaper than most brands\' entry models. Silver resistivity is about eight per cent below copper, which at line level over a metre is a difference the engine computes and reports as zero to two decimal places.',
    provenanceNote:
      'Silver conductor and PTFE dielectric are manufacturer statements; gauge is estimated and no electrical figures are published.',
  }),

  makeCable({
    mfr: 'Signal Cable',
    model: 'Classic Speaker Cable',
    cableType: 'speaker',
    price: usd(110, 'CURRENT_RETAIL', 2024, 'Direct-sale price for a 2.4 m terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#6b8f3a',
    tags: ['entry-line', 'direct-sale', 'budget'],
    notes:
      'A 12 AWG oxygen-free copper twisted pair with soldered terminations, sold direct. About 0.025 ohm of loop resistance over 2.4 m -- the same figure a cable costing thirty times more with the same gauge would produce, because gauge is what sets it.',
    provenanceNote:
      'Gauge is a stated figure for this cable; resistance, inductance and capacitance are not published and are derived from gauge and geometry.',
  }),

  makeCable({
    mfr: 'Signal Cable',
    model: 'Ultra Speaker Cable',
    cableType: 'speaker',
    price: usd(190, 'CURRENT_RETAIL', 2024, 'Direct-sale price for a 2.4 m terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 10,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#6b8f3a',
    tags: ['heavy-gauge', 'direct-sale', 'budget'],
    notes:
      'The heavier copper cable in the line, roughly 10 AWG, which takes loop resistance over 2.4 m down to about 0.016 ohm. Worth having on a long run into a low-impedance loudspeaker and largely academic on a two-metre run into an easy one.',
    provenanceNote:
      'Gauge is stated by the maker; no per-metre electrical figures are published, so all three are derived.',
  }),

  makeCable({
    mfr: 'Signal Cable',
    model: 'Silver Resolution Speaker Cable',
    cableType: 'speaker',
    price: usd(620, 'CURRENT_RETAIL', 2024, 'Direct-sale price for a 2.4 m terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 12,
    dielectric: 'Teflon (PTFE)',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#6b8f3a',
    tags: ['silver-conductor', 'ptfe', 'direct-sale'],
    notes:
      'Silver conductors in PTFE for loudspeaker use. Silver buys about eight per cent lower resistance than copper of the same cross-section, so this cable measures close to a 12 AWG copper one -- rather than close to a 10 AWG one, which costs a third as much.',
    provenanceNote:
      'Conductor material and dielectric are stated; gauge is estimated from the cable and no electrical figures are published.',
  }),

  makeCable({
    mfr: 'Signal Cable',
    model: 'MagicPower Classic',
    cableType: 'power',
    price: usd(80, 'CURRENT_RETAIL', 2024, 'Direct-sale price for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#6b8f3a',
    tags: ['entry-line', 'direct-sale', 'budget'],
    notes:
      'A 12 AWG three-core mains cord with hospital-grade plug hardware, sold direct for less than most brands charge for a connector. The engine applies no signal processing to power cords, and this file will not pretend the plug hardware does anything to the audio.',
    provenanceNote:
      'Gauge is a stated figure. No electrical data is published, and none is read by the engine for power cords.',
  }),

  makeCable({
    mfr: 'Signal Cable',
    model: 'MagicPower Digital Reference',
    cableType: 'power',
    price: usd(140, 'CURRENT_RETAIL', 2024, 'Direct-sale price for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 15,
    shielding: 'Foil shield with drain, tied at the plug end',
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#6b8f3a',
    tags: ['shielded', 'direct-sale'],
    notes:
      'The shielded version of the MagicPower cord, with the shield drained at the plug end so it forms a single-ended enclosure around the mains conductors. That is a real and conventional way to keep cord radiation out of nearby signal cabling; it is not modelled here, because the engine applies no DSP to power cords.',
    provenanceNote:
      'Shield construction and gauge are stated by the maker. No per-metre electrical figures exist for this cord and none would be used.',
  }),

  makeCable({
    mfr: 'Signal Cable',
    model: 'MagicPower Silver Resolution',
    cableType: 'power',
    price: usd(330, 'CURRENT_RETAIL', 2024, 'Direct-sale price for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 20,
    shielding: 'Shielded',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#6b8f3a',
    tags: ['high-current', 'shielded', 'direct-sale'],
    notes:
      'The top mains cord in the range: heavier conductors, a shield and 20 A connector options for amplifiers. Cross-section is the specification that matters for delivering current to a power supply, and nothing about it reaches the signal path in this engine.',
    provenanceNote:
      'Gauge inferred from the 20 A connector options and cable diameter. The line name refers to the range tier rather than to a silver mains conductor, so copper is used here.',
  }),

  // =========================================================================
  // Blue Jeans Cable -- United States
  // =========================================================================
  makeCable({
    mfr: 'Blue Jeans Cable',
    model: 'LC-1 Audio Interconnect',
    cableType: 'interconnect',
    price: usd(35, 'CURRENT_RETAIL', 2024, 'Price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 24,
    capacitancePfPerM: 40,
    electricalConfidence: 'MANUFACTURER_SPEC',
    electricalSource: 'Blue Jeans Cable published capacitance for LC-1, 12.2 pF/ft',
    shielding: 'Double braid, foil and braid, shield used as the return',
    dielectric: 'Foamed polyethylene',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3a6ea5',
    tags: ['low-capacitance', 'published-specs', 'value'],
    notes:
      'Built around a thick foamed dielectric and a small centre conductor, which is how it reaches 12.2 pF per foot -- roughly 40 pF/m, about half a conventional interconnect. Against a 600 ohm tube output a 1 m run puts the low-pass corner near 6 MHz, so the in-band result the engine computes is flat.',
  }),

  makeCable({
    mfr: 'Blue Jeans Cable',
    model: 'Belden 1694A Digital Coax',
    cableType: 'interconnect',
    price: usd(30, 'CURRENT_RETAIL', 2024, 'Price for a 1 m single cable.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 18,
    capacitancePfPerM: 53,
    characteristicOhms: 75,
    electricalConfidence: 'MANUFACTURER_SPEC',
    electricalSource: 'Belden 1694A datasheet, 16.2 pF/ft and 75 ohm nominal impedance',
    shielding: 'Foil plus tinned copper braid',
    dielectric: 'Gas-injected foam HDPE',
    termination: ['RCA', 'BNC'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3a6ea5',
    tags: ['digital-coax', '75-ohm', 'published-specs', 'broadcast'],
    notes:
      'A broadcast video coax with an 18 AWG solid copper centre and a gas-injected foam dielectric, held to 75 ohms with genuine tolerance rather than by assertion. It is filed as an interconnect because that is the construction; impedance match, which is what a digital coax link actually depends on, is outside what this engine models.',
  }),

  makeCable({
    mfr: 'Blue Jeans Cable',
    model: 'Canare L-4E6S Quad',
    cableType: 'interconnect',
    price: usd(40, 'CURRENT_RETAIL', 2024, 'Price for a 1 m XLR pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'star-quad',
    gauge: 21,
    shielding: 'Spiral copper shield',
    dielectric: 'Polyethylene',
    termination: ['XLR', 'TRS'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3a6ea5',
    tags: ['star-quad', 'balanced', 'pro-audio', 'value'],
    notes:
      'Canare star-quad terminated by Blue Jeans: four conductors paralleled in opposing pairs so that the loop area presented to an external magnetic field nearly cancels. That is a genuine and measurable interference rejection mechanism, and it is the reason this construction is standard in studios.',
    provenanceNote:
      'Construction and conductor arrangement are from the Canare datasheet. The effective gauge given is the two paralleled conductors per leg; capacitance and inductance here are derived from the star-quad geometry rather than taken from the datasheet.',
  }),

  makeCable({
    mfr: 'Blue Jeans Cable',
    model: 'Belden 5100UE',
    cableType: 'speaker',
    price: usd(45, 'CURRENT_RETAIL', 2024, 'Price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 16,
    resistanceOhmPerM: 0.0264,
    electricalConfidence: 'MANUFACTURER_SPEC',
    electricalSource: 'Belden 5100UE datasheet conductor DC resistance, 4.0 ohm/1000 ft',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3a6ea5',
    tags: ['published-specs', 'value', 'cl3-rated'],
    notes:
      'The lightest of the Belden speaker cables Blue Jeans stocks, two 16 AWG conductors twisted under a jacket. A 3 m run gives about 0.08 ohm of loop resistance -- roughly 0.17 dB into a 4 ohm load, and enough to more than double the source impedance a driver sees from a well-behaved amplifier. This is the cable in the file where gauge starts to matter.',
    provenanceNote:
      'Conductor DC resistance is the published Belden figure. Inductance and capacitance are derived from the twisted-pair geometry, as Belden does not list them for this construction.',
  }),

  makeCable({
    mfr: 'Blue Jeans Cable',
    model: 'Belden 5200UE',
    cableType: 'speaker',
    price: usd(60, 'CURRENT_RETAIL', 2024, 'Price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    resistanceOhmPerM: 0.0166,
    electricalConfidence: 'MANUFACTURER_SPEC',
    electricalSource: 'Belden 5200UE datasheet conductor DC resistance, 2.5 ohm/1000 ft',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3a6ea5',
    tags: ['published-specs', 'value', 'cl3-rated'],
    notes:
      'Two 14 AWG conductors twisted under a CL3-rated jacket, which makes it legal for in-wall use. About 0.05 ohm over a 3 m run, or 0.11 dB into 4 ohms -- a third less loss than the 16 AWG cable for a few dollars more.',
    provenanceNote:
      'Conductor DC resistance is the published Belden figure; inductance and capacitance are derived from geometry, which Belden does not specify for this cable.',
  }),

  makeCable({
    mfr: 'Blue Jeans Cable',
    model: 'Belden 5000UE',
    cableType: 'speaker',
    price: usd(80, 'CURRENT_RETAIL', 2024, 'Price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    resistanceOhmPerM: 0.0104,
    electricalConfidence: 'MANUFACTURER_SPEC',
    electricalSource: 'Belden 5000UE datasheet conductor DC resistance, 1.59 ohm/1000 ft',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3a6ea5',
    tags: ['published-specs', 'value', 'cl3-rated'],
    notes:
      'The 12 AWG member of the family and the sensible default for most rooms: about 0.03 ohm of loop resistance over 3 m, or 0.07 dB into a 4 ohm load. Everything above this gauge in the file buys smaller and smaller fractions of a decibel.',
    provenanceNote:
      'Conductor DC resistance is published by Belden; L and C are derived from the twisted-pair geometry.',
  }),

  makeCable({
    mfr: 'Blue Jeans Cable',
    model: 'Belden 5T00UP',
    cableType: 'speaker',
    price: usd(120, 'CURRENT_RETAIL', 2024, 'Price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 10,
    resistanceOhmPerM: 0.0066,
    electricalConfidence: 'MANUFACTURER_SPEC',
    electricalSource: 'Belden 5T00UP datasheet conductor DC resistance, 1.0 ohm/1000 ft',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3a6ea5',
    tags: ['published-specs', 'heavy-gauge', 'value'],
    notes:
      'The heaviest Belden speaker cable in the range at 10 AWG per conductor, for long runs or difficult loads: roughly 0.02 ohm over 3 m, about 0.04 dB into 4 ohms. Useful on a 10 m run to a low-impedance loudspeaker, and hard to justify on a short one.',
    provenanceNote:
      'Conductor DC resistance is the published Belden figure. Inductance and capacitance are derived, as Belden does not publish them for this construction.',
  }),

  makeCable({
    mfr: 'Blue Jeans Cable',
    model: 'Belden 1700A Cat 6',
    cableType: 'ethernet',
    price: usd(14, 'CURRENT_RETAIL', 2024, 'Price for a 2 m patch lead.'),
    priceLengthM: 2,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 23,
    characteristicOhms: 100,
    digitalStandard: 'Cat 6, tested to 250 MHz',
    shielding: 'U/UTP, bonded pairs',
    termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3a6ea5',
    tags: ['cat6', 'published-specs', 'tested'],
    notes:
      'Belden bonded-pair Cat 6 with 23 AWG solid conductors, terminated and individually tested for wiremap, crosstalk and return loss before shipping. Bonding holds the two conductors of a pair at a constant spacing so the impedance stays near 100 ohms through bends. The engine applies no signal processing to ethernet.',
  }),

  makeCable({
    mfr: 'Blue Jeans Cable',
    model: 'Belden 10GX32 Cat 6a',
    cableType: 'ethernet',
    price: usd(20, 'CURRENT_RETAIL', 2024, 'Price for a 2 m patch lead.'),
    priceLengthM: 2,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 23,
    characteristicOhms: 100,
    digitalStandard: 'Cat 6a, tested to 500 MHz',
    shielding: 'U/UTP with a bonded-pair core designed for alien crosstalk margin',
    termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3a6ea5',
    tags: ['cat6a', 'published-specs', 'tested', '10gbase-t'],
    notes:
      'Cat 6a rated to 500 MHz for 10GBASE-T over its full channel length, with the pair geometry and separator designed to control crosstalk between adjacent cables in a bundle. That is the entire benefit over Cat 6, and it is a networking benefit: no signal processing is applied to ethernet here.',
  }),

  makeCable({
    mfr: 'Blue Jeans Cable',
    model: 'Belden USB 2.0 A-B',
    cableType: 'usb',
    price: usd(22, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid over a twisted data pair and separate power pair',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3a6ea5',
    tags: ['usb-2.0', 'compliant', 'value'],
    notes:
      'A compliant USB 2.0 assembly built from Belden bulk cable with 24 AWG power conductors and a shielded 90 ohm data pair. USB audio is packetised and error-checked, and an asynchronous DAC clocks the data itself, so a compliant cable either delivers the packets or fails outright. No signal processing is applied.',
  }),
];
